// Protects quotes from small wording differences between Typeform and the
// price file (product-data.json).
//
// 1. Labels are compared ignoring extra spaces and capital letters, so
//    "Medium (500  - 950 sq. ft.)" in Typeform still finds
//    "Medium (500 - 950 sq. ft.)" in the price file.
// 2. Every answer to a question that affects the price must be one we know.
//    If Typeform wording changes, the quiz stops and hands the lead to a
//    specialist instead of showing a wrong price.
//
// KNOWN_ANSWERS is a copy of the live Typeform choices ("Heatpump Quiz
// (PROD)", form uFpNs2Bt, checked 7 Oct 2026). When an answer is reworded or
// added in Typeform, update it here AND in the price file in the same change.

export const normalizeLabel = (value) =>
  String(value).replace(/\s+/g, " ").trim().toLowerCase();

// Looks up a price-file entry by answer label, tolerating spacing/case.
export const lookupByLabel = (table, value) => {
  if (!table || value === undefined || value === null) return undefined;
  if (Object.prototype.hasOwnProperty.call(table, value)) return table[value];
  const wanted = normalizeLabel(value);
  const key = Object.keys(table).find((k) => normalizeLabel(k) === wanted);
  return key === undefined ? undefined : table[key];
};

const ROOM_SIZES = [
  "Small (less than 500 sq. ft.)",
  "Medium (500 - 950 sq. ft.)",
  "Large (950 - 1300 sq. ft.)",
  "Extra Large (over 1300 sq. ft.)",
];
const HOME_SQ_FT = ["Under 1000", "1000 - 2400", "2400 - 3600", "Over 3600"];
const OUTDOOR_UNIT = ["Ground level pad", "Ground level wall bracket"];

export const KNOWN_ANSWERS = {
  heating_type: [
    "Forced air furnace or central heat pump",
    "Boiler or radiators",
    "Stove",
    "Ductless mini-split",
    "Electric baseboards",
    "Other",
  ],
  heat_source: ["Oil", "Propane", "Electricity", "Natural Gas", "Wood", "Other"],
  electric_panel: ["60 amp", "100-125 amp", "200 amp", "Unsure"],
  system_age: ["Over 20 years old", "Under 20 years old", "Unsure"],
  air_conditioner: ["true", "false"],
  "sq_ft-Central": HOME_SQ_FT,
  "sq_ft-Ductless": HOME_SQ_FT,
  condenser_position: OUTDOOR_UNIT,
  condenser_mounted: OUTDOOR_UNIT,
  room_number: ["1", "2", "3", "4", "5+"],
  rooms_1_size: ROOM_SIZES,
  "rooms_2_size-room_1": ROOM_SIZES,
  "rooms_2_size-room_2": ROOM_SIZES,
  "rooms_3_size-room_1": ROOM_SIZES,
  "rooms_3_size-room_2": ROOM_SIZES,
  "rooms_3_size-room_3": ROOM_SIZES,
  "rooms_4_size-room_1": ROOM_SIZES,
  "rooms_4_size-room_2": ROOM_SIZES,
  "rooms_4_size-room_3": ROOM_SIZES,
  "rooms_4_size-room_4": ROOM_SIZES,
};

const KNOWN_NORMALIZED = Object.fromEntries(
  Object.entries(KNOWN_ANSWERS).map(([ref, labels]) => [
    ref,
    new Set(labels.map(normalizeLabel)),
  ])
);

export class UnknownAnswerError extends Error {
  constructor(ref, value) {
    super(`Unrecognised answer for ${ref}: ${value}`);
    this.name = "UnknownAnswerError";
    this.ref = ref;
    this.value = value;
  }
}

// The value a Typeform answer carries (choice label, text or yes/no).
export const answerValue = (answer) =>
  answer.choice ? answer.choice.label : answer.text ?? answer.boolean;

// Throws UnknownAnswerError for the first price-affecting answer we don't know.
export const assertKnownAnswers = (answers = []) => {
  for (const answer of answers) {
    const ref = answer?.field?.ref;
    const known = KNOWN_NORMALIZED[ref];
    if (!known) continue;
    const value = answerValue(answer);
    if (value === undefined || value === null || !known.has(normalizeLabel(value))) {
      throw new UnknownAnswerError(ref, value);
    }
  }
};
